/**
 * AI Resolution Assistant API Service
 * Handles document matching, email drafting, and priority scoring (Phase 3.5c)
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export interface DocumentMatch {
  document_id: string;
  document_name: string;
  document_type: string;
  match_confidence: number;
  relevance_explanation: string;
  suggested_action: string;
}

export interface EmailDraft {
  subject: string;
  body: string;
  urgency: 'LOW' | 'MEDIUM' | 'HIGH';
  missing_items: string[];
  deadline_suggestion?: string;
}

export interface PriorityScore {
  issue_id: string;
  priority_score: number;
  blocking_calculations: string[];
  affected_amount: number;
  urgency_reason: string;
}

export interface DocumentMatchResponse {
  issue_id: string;
  matches: DocumentMatch[];
}

export interface EmailDraftResponse {
  draft: EmailDraft;
  issue_count: number;
}

export interface PriorityScoreResponse {
  scores: PriorityScore[];
  total_issues: number;
}

class AIResolutionAssistantApi {
  private async getHeaders(): Promise<HeadersInit> {
    return {
      'Content-Type': 'application/json'
    };
  }

  async healthCheck(): Promise<{ status: string; ai_available: boolean }> {
    const response = await fetch(`${BACKEND_URL}/api/v1/ai-assistant/health`);
    if (!response.ok) {
      throw new Error('AI Assistant service unavailable');
    }
    return response.json();
  }

  async matchDocuments(issueId: string): Promise<DocumentMatchResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/ai-assistant/match-documents`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ issue_id: issueId })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to match documents');
    }
    return response.json();
  }

  async draftEmail(
    issueIds: string[],
    clientName: string,
    caseReference: string,
    senderName?: string
  ): Promise<EmailDraftResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/ai-assistant/draft-email`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        issue_ids: issueIds,
        client_name: clientName,
        case_reference: caseReference,
        sender_name: senderName || 'Claims Team'
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to draft email');
    }
    return response.json();
  }

  async calculatePriorityScores(
    options: { issueIds?: string[]; reportId?: string } = {}
  ): Promise<PriorityScoreResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/ai-assistant/priority-scores`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        issue_ids: options.issueIds,
        report_id: options.reportId
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to calculate priority scores');
    }
    return response.json();
  }

  async batchMatchDocuments(reportId: string): Promise<{
    report_id: string;
    issues_processed: number;
    issues_with_matches: number;
    results: Array<{
      issue_id: string;
      issue_title: string;
      matches: DocumentMatch[];
    }>;
  }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/ai-assistant/batch-match/${reportId}`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to batch match documents');
    }
    return response.json();
  }
}

export const aiAssistantApi = new AIResolutionAssistantApi();
export default aiAssistantApi;
